/**
 * The `<table>` itself: header row, body, and nothing else.
 *
 * Split from `DataTable` only because the flip changes what a ROW IS — upright
 * the rows are the report's own records, flipped they are its columns — and a
 * component cannot be generic in two row types at once. Everything that does
 * not care which way round it is lives here; everything that does lives in
 * `DataTable`, which picks an orientation and hands it over.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cellClass, cols, declaresWidths, headCellClass, tableClass, type Density } from './tableLayout';
import { keepGroups, nextSort, sortRows, type Column, type Sort } from './tableColumns';
import { gutterWidth, rowNumber } from './rowNumbers';
import { useSeen, type SeenMarks } from '../useSeen';
import NewHere from './NewHere';

// THE FROZEN COLUMN, and two ways it leaks.
//
// z-10: a sticky cell comes first in its row, so without a stacking context
// every cell after it paints straight over the top as the table scrolls under.
//
// pl-3, with no horizontal padding on the scroll container: `left-0` sticks to
// the scrollport edge, and a container with `p-3` puts twelve pixels of
// scrollable area to the LEFT of that — so the table slid visibly through the
// gap beside the frozen column. The inset lives on the cell, which moves with
// it. The hairline on the right is what tells a reader the column is frozen
// rather than merely first.
//
// A row lights up under the pointer so the eye can follow it across. The
// frozen cell paints its own background, so it has to light up too, and in a
// solid colour: anything see-through would show the cells scrolling under it.
const ROW_HOVER = 'group/row hover:bg-gray-50 dark:hover:bg-[#26303f]';
const STICKY =
  'sticky left-0 z-10 bg-white dark:bg-gray-800 pl-3 pr-3 ' +
  'group-hover/row:bg-gray-50 dark:group-hover/row:bg-[#26303f] ' +
  'shadow-[1px_0_0_0_rgba(0,0,0,0.10)] dark:shadow-[1px_0_0_0_rgba(255,255,255,0.10)]';

/* AND ROOM ON THE OTHER SIDE OF IT. Cells carry padding on the right only —
   the gap between two columns is the left one's `pr-2`, which works everywhere
   except here: the frozen column's padding is INSIDE the sticky element, so it
   travels with that column and leaves nothing between its hairline and the
   next column's text. Every row read as though the second column had been
   pushed up against the first. The inset belongs to the cell that follows,
   where it cannot scroll away, and only to that one — putting it on every cell
   would widen a thirty-column matrix by a quarter of the screen. */
const AFTER_STICKY = 'pl-3';

/* WHEN A HEADER IS WORTH TURNING ON ITS SIDE.
 *
 * Sideways headers are not a style, they are what you do when there is no room
 * to write the names across — sixty products cannot each have their title
 * along the top. With two, there is plenty of room, and turning them anyway
 * bought a hundred-pixel band of empty space above a table two columns wide,
 * with each name stranded at the far left of a column its dot sat in the
 * middle of. So a table asks to turn its headers and this decides whether it
 * needs to. */
const UPRIGHT_MAX = 6;


/* HOW FAR A SIDEWAYS HEADER MAY RUN.
 *
 * Turned on its side, a header's LENGTH is the band's HEIGHT — so the one
 * longest name sets it for all of them, and "Automation Modes (Co-Pilot/
 * Auto-Pilot)" pushed a third of a screen of empty band above every table.
 * The band is measured from the longest name and capped; the overrun ellipses,
 * and the whole name stays on the cell's title. */
const SIDEWAYS_CAP = 144;
const SIDEWAYS_MIN = 48;
/** Roughly a character of the 10px face the names are drawn in. */
const PER_CHAR = 6.2;

/* How many characters a turned header has room for at full height. Callers
   that draw their own header need this to know what they can fit: the band is
   capped, the run is clipped at the end, and what sits at the end is usually
   the count — the one part of the header that is no use half-printed. */
export const SIDEWAYS_CHARS = Math.floor(SIDEWAYS_CAP / PER_CHAR);

function bandHeight(labels: string[]): number {
  const longest = labels.reduce((n, l) => Math.max(n, l.length), 0);
  return Math.min(SIDEWAYS_CAP, Math.max(SIDEWAYS_MIN, Math.round(longest * PER_CHAR)));
}

/* DRAWN A SLICE AT A TIME. Every row of every table used to be drawn at once:
   the feature matrix on room 41 is 517 features by 277 products in one group,
   about 190,000 cells over the page, and opening it took four seconds. A table
   now draws about CELL_BUDGET cells' worth of rows, and more as you scroll to
   the end of it; past COL_MAX columns the rest wait behind "Show all". Sorting,
   the text filter, the row numbers and the CSV still see every row. */
const CELL_BUDGET = 4000;
const ROWS_MIN = 25;
const ROWS_MAX = 200;
const COL_MAX = 120;

/** How many rows one slice is, for a table this wide. Pure. */
export function rowStep(columnCount: number): number {
  return Math.min(ROWS_MAX, Math.max(ROWS_MIN, Math.floor(CELL_BUDGET / Math.max(1, columnCount))));
}

/** How many rows to draw: the slices asked for, and always through the last
 *  row marked new, so "jump to the new ones" has a row to go to. Pure. */
export function rowsToDraw(total: number, limit: number, lastNew: number): number {
  return Math.min(total, Math.max(limit, lastNew + 1));
}

export interface GridProps<R> {
  columns: Column<R>[];
  rows: R[];
  rowKey: (row: R) => string | number;
  mode: Density;
  sort: Sort | null;
  onSort: (next: Sort | null) => void;
  axis: 'upright' | 'flipped';
  /** Number the rows down the left, as they sit on screen. A reading aid, so
   *  it is not a column and the file never carries it. */
  numbered?: boolean;
  section?: (row: R) => string | null;
  detail?: (row: R) => ReactNode;
  expanded?: (row: R) => boolean;
  empty?: ReactNode;
  rowClass?: (row: R) => string;
  rowData?: (row: R) => Record<string, string>;
  testId?: string;
  /** Ids that changed and have not been looked at. A row carrying one is lit
   *  until it is genuinely on screen, then fades.
   *
   *  ONLY FOR A TABLE THAT IS THE WHOLE REPORT. Where a report draws several —
   *  a matrix per company, a price table per solution type, and the questions
   *  list under all of them — it must build ONE tracker and pass it as `seen`
   *  instead. See the note on `seen`. */
  unseen?: ReadonlySet<number>;
  /** Called with ids now on screen — the caller drops them from the badge,
   *  which reads from the same set. */
  onSeen?: (ids: number[]) => void;
  /** THE REPORT'S OWN TRACKER, where the report has more than one table.
   *
   *  Handed `unseen` directly, every table built a tracker of its own, and a
   *  tracker drops anything waiting that it has no row for — so the first one
   *  to render pruned the marks belonging to all the others, and the questions
   *  list, whose rows are a handful of open questions, pruned nearly the whole
   *  report. The tab said four, you opened it, and there was nothing lit and
   *  no button: the marks had been thrown away between the badge and the row.
   *
   *  One tracker for the report fixes the counting too — it knows every row in
   *  every table, so "4 new here" is four rows you can be taken to. */
  seen?: SeenMarks;
  /** The row's item id, when it is not the row key. */
  rowItemId?: (row: R) => number;
  /** The row's ref, where the CALLER does its own unread tracking. The
   *  landscape stacks a table per axis over one set of players, and a tracker
   *  per table would each prune the others' marks as ids it has no row for. */
  rowRef?: (row: R) => (el: HTMLElement | null) => void;
}

export default function TableGrid<R>({
  columns: allColumns,
  rows,
  rowKey,
  mode,
  sort,
  onSort,
  axis,
  numbered,
  section,
  detail,
  expanded,
  empty,
  rowClass,
  rowData,
  testId,
  unseen,
  onSeen,
  seen: given,
  rowItemId,
  rowRef,
}: GridProps<R>) {
  // Sorted by the column asked for, but never across a band: see `keepGroups`.
  const sorted = sortRows(rows, allColumns, sort);
  const ordered = section ? keepGroups(sorted, rows, section) : sorted;
  const [allCols, setAllCols] = useState(false);
  const columns = allCols || allColumns.length <= COL_MAX + 1 ? allColumns : allColumns.slice(0, COL_MAX + 1);
  const hiddenCols = allColumns.length - columns.length;
  const step = rowStep(columns.length);
  const [limit, setLimit] = useState(step);
  /* THE FROZEN COLUMN IS NOT A LONG-LINE FEATURE. It was tied to that density
     because that was the only one that scrolled; now every density does, and a
     row whose label has scrolled out of sight is unreadable in all of them
     equally. What a row IS stays on screen whatever the cells are doing. */
  const wide = true;
  /* WHO GETS THE LEFTOVER WIDTH. `long` is `table-auto w-full`, and HTML
     auto-layout hands surplus out in proportion to how wide each column's
     CONTENT already is — so the widest column takes the biggest share of it.
     On a sparse table that is the label column: eleven products and five
     columns of "…" gave `compared on` four hundred pixels to hold a phrase
     needing two hundred, while the products sprawled at two-seventy apiece.
     An empty column set to the full width absorbs the surplus instead, so
     every real column sizes to what is in it and the table still fills its
     card. With no surplus — a matrix of forty-seven products — it collapses
     to nothing and changes not a pixel. */
  const spacer = !declaresWidths(mode);
  // The gutter counts: a section band or an empty line spanning one column too
  // few leaves a hole where the numbers are.
  const span = columns.length + (spacer ? 1 : 0) + (numbered ? 1 : 0);
  // WHERE EACH ROW SITS ON SCREEN, after the sort. Numbers run 1..n down the
  // page with no gaps — they are a position, not an identity, so re-sorting
  // renumbers rather than carrying the old order along.
  const at = new Map(ordered.map((row, i) => [rowKey(row), i]));
  let band: string | null = null;

  /* Turned only when there are too many to write across. Below that the names
     go along the top like anybody's table. */
  const asked = columns.filter(c => c.vertical);
  const sideways = asked.length > UPRIGHT_MAX;
  /* An explicit height, not a maximum: `max-height` on a table cell is
     undefined behaviour, and the cell collapsed to a single line while its
     names went on painting down over the first three rows. */
  // Measured off what RENDERS, not off the column's name — see `measure`.
  const headHeight = sideways
    ? `${bandHeight(asked.map(c => c.measure ?? c.label))}px`
    : undefined;

  // LIT UNTIL LOOKED AT. The row carrying an item the room says changed stays
  // marked however long the tab is closed, and stops when it has actually been
  // on screen — not when the report was opened, because the three rows that
  // changed can be at the bottom of forty.
  const noop = useCallback(() => {}, []);
  const idOf = (row: R): number => {
    if (rowItemId) return rowItemId(row);
    // The id is the tail of the key — `competitor-88`, `price-row-12`. A key
    // with no number on the end is not an item: `demand-term-online-booking`
    // used to strip to nothing and read as id 0, which made every row of that
    // table claim the same id and prune every mark in the room.
    const tail = String(rowKey(row)).match(/(\d+)$/);
    return tail ? Number(tail[1]) : NaN;
  };
  const ids = ordered.map(idOf);
  /* A TABLE WITH NO ITEM IDS HAS NOTHING TO MARK, and must not be handed the
     set: the tracker clears anything waiting that it has no row for, so a
     table of attributes would silently drop every mark in the room. */
  const trackable = ids.some(Number.isFinite);
  /* The hook runs either way — a component may not call a different number of
     them between renders — and is inert when the report brought its own. */
  const own = useSeen(
    given || !trackable ? undefined : unseen,
    given ? noop : (onSeen ?? noop),
    given || !trackable ? undefined : ids,
  );
  const seen = given ?? own;
  let lastNew = -1;
  if (ordered.length > limit) {
    for (let i = ordered.length - 1; i > limit; i -= 1) {
      if (seen.cls(idOf(ordered[i])).includes('jc-unseen')) {
        lastNew = i;
        break;
      }
    }
  }
  const drawn = rowsToDraw(ordered.length, limit, lastNew);
  const shown = drawn < ordered.length ? ordered.slice(0, drawn) : ordered;
  const left = ordered.length - drawn;
  // MORE AS YOU REACH THE END: the line under the table asks for the next
  // slice when it scrolls into view. Without IntersectionObserver (tests, old
  // browsers) it is a button.
  const moreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = moreRef.current;
    if (!el || left <= 0 || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) setLimit(n => n + step);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [left, step]);

  return (
    <>
    {/* WHERE the new ones are, not just how many. Scrolling a forty-row table
        hoping to pass the right three is not finding them. A report that owns
        the tracker draws this itself, ONCE, above all of its tables. */}
    {!given && <NewHere count={seen.waitingHere} glowing={seen.glowing} onJump={seen.next} onClearAll={seen.readAll} />}
    {hiddenCols > 0 && (
      <div className="py-1 text-xs text-gray-500 dark:text-gray-400" data-testid="table-more-columns">
        {(columns.length - 1).toLocaleString()} of {(allColumns.length - 1).toLocaleString()} columns shown{' '}
        <button type="button" onClick={() => setAllCols(true)} className="underline hover:no-underline">
          Show all {(allColumns.length - 1).toLocaleString()}
        </button>
      </div>
    )}
    <table
      className={`text-xs border-collapse ${tableClass(mode)}`}
      data-testid={testId}
      data-axis={axis}
    >
      {/* `long` declares no widths at all — a colgroup and `table-auto` argue,
          and the colgroup wins, which is the whole point of long line. */}
      {declaresWidths(mode) && (
        <colgroup>
          {/* The gutter is a real column so the widths below still add up —
              a number drawn inside the first cell would push its content
              around and the sticky first column would carry it sideways. */}
          {numbered && <col style={{ width: `${gutterWidth(ordered.length)}ch` }} />}
          {cols(columns.map(c => c.width ?? 10)).map((c, i) => (
            <col key={i} style={c} />
          ))}
        </colgroup>
      )}
      <thead className="text-gray-400">
        <tr className="text-left">
          {numbered && (
            <th scope="col" className="py-1 pr-2 text-right font-normal" aria-label="Row number">
              #
            </th>
          )}
          {columns.map((c, i) => {
            const sortable = c.sortable !== false;
            const active = sort?.key === c.key;
            const arrow = active ? (sort!.dir === 'asc' ? '↑' : '↓') : '⇅';
            const title = active
              ? sort!.dir === 'asc'
                ? 'Sorted low to high · click for high to low'
                : 'Sorted high to low · click to go back to the board’s own order'
              : `Sort by ${c.label}`;
            return (
              <th
                key={c.key}
                scope="col"
                title={c.vertical && sideways ? c.label : undefined}
                className={`font-normal py-1 ${c.align === 'right' ? 'text-right' : ''} ${
                  i === 0 && wide ? STICKY : i === 1 && wide ? AFTER_STICKY : ''
                } ${c.vertical && sideways ? 'px-1 align-middle' : 'align-bottom'}`}
                style={
                  c.vertical && sideways
                    ? {
                        writingMode: 'vertical-rl',
                        /* Turned sideways the INLINE axis runs down the page,
                           so `end` sits the names on the table rather than
                           leaving them ragged in mid-air. `align-bottom`
                           cannot do it — block-end here is a SIDE, which is
                           what stranded every name at the left of its own
                           column. */
                        textAlign: 'end',
                        height: headHeight,
                      }
                    : undefined
                }
                aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
              >
                {c.vertical && sideways ? (
                  /* One rotated run of text and nothing else. A flex box and a
                     sort control in here laid out along the wrong axis and
                     painted outside the cell — the names drifted a column and
                     a half clear of the dots they belonged to. */
                  <span
                    className="rotate-180 inline-block whitespace-nowrap overflow-hidden"
                    style={{ maxHeight: headHeight, textOverflow: 'ellipsis' }}
                  >
                    {c.head ?? c.label}
                  </span>
                ) : c.head ? (
                  /* A drawn header does its own job — opening the product it
                     names — so sorting gets its own small control beside it
                     rather than stealing the click. */
                  <span className="inline-flex items-baseline gap-0.5">
                    {c.head}
                    {sortable && (
                      <button
                        type="button"
                        onClick={() => onSort(nextSort(sort, c.key))}
                        className={`hover:text-gray-600 dark:hover:text-gray-200 ${active ? '' : 'opacity-40'}`}
                        title={title}
                        // Just "Sort": the header it sits in already says
                        // which column, and `Sort by Vagaro` beside a button
                        // called `Vagaro` is two controls with one name.
                        aria-label="Sort"
                        data-testid={`sort-${c.key}`}
                      >
                        {arrow}
                      </button>
                    )}
                  </span>
                ) : sortable ? (
                  <button
                    type="button"
                    onClick={() => onSort(nextSort(sort, c.key))}
                    className="hover:text-gray-600 dark:hover:text-gray-200"
                    title={title}
                    data-testid={`sort-${c.key}`}
                  >
                    {c.label}
                    {/* The arrow only appears on the column doing the work: a
                        marker on every header is a marker on none. */}
                    {active && <span className="ml-0.5">{sort!.dir === 'asc' ? '↑' : '↓'}</span>}
                  </button>
                ) : (
                  c.label
                )}
              </th>
            );
          })}
          {spacer && <th aria-hidden className="w-full p-0" />}
        </tr>
      </thead>
      <tbody>
        {shown.map(row => {
          const here = section?.(row) ?? null;
          const opensBand = here !== null && here !== band;
          if (opensBand) band = here;
          const open = expanded?.(row) ?? false;
          const under = open ? detail?.(row) : null;
          return [
            opensBand && (
              <tr key={`section-${here}`} data-testid={`section-${here}`}>
                <td colSpan={span} className="pt-3 pb-1 text-[11px] uppercase tracking-wide text-gray-400">
                  {here}
                </td>
              </tr>
            ),
            <tr
              key={rowKey(row)}
              ref={rowRef ? rowRef(row) : seen.mark(idOf(row))}
              className={`border-t border-gray-200 dark:border-gray-700 align-top ${ROW_HOVER} ${seen.cls(
                idOf(row),
              )} ${rowClass?.(row) ?? ''}`}
              data-testid={String(rowKey(row))}
              {...(rowData?.(row) ?? {})}
            >
              {numbered && (
                <td
                  className="py-1.5 pr-2 text-right tabular-nums text-gray-400 dark:text-gray-500 select-none"
                  data-testid={`rownum-${rowKey(row)}`}
                >
                  {rowNumber(at.get(rowKey(row)) ?? 0)}
                </td>
              )}
              {columns.map((c, i) => {
                const align = c.cellAlign?.(row) ?? c.align;
                const className = `py-1.5 pr-2 ${i === 1 && wide ? AFTER_STICKY : ''} ${
                  align === 'right' ? 'text-right tabular-nums' : ''
                } ${i === 0 ? headCellClass(mode) : cellClass(mode)} ${c.cellClass?.(row) ?? ''}`;
                const content = c.cell ? c.cell(row) : (c.value(row) ?? '');
                return i === 0 ? (
                  <th
                    key={c.key}
                    scope="row"
                    className={`font-normal text-left ${className} ${wide ? STICKY : ''}`}
                  >
                    {content}
                  </th>
                ) : (
                  <td key={c.key} className={className} data-testid={c.cellId?.(row) || undefined}>
                    {content}
                  </td>
                );
              })}
              {spacer && <td aria-hidden className="p-0" />}
            </tr>,
            under && (
              <tr key={`detail-${rowKey(row)}`} data-testid={`detail-${rowKey(row)}`}>
                <td colSpan={span} className="pb-2">
                  {under}
                </td>
              </tr>
            ),
          ];
        })}
        {ordered.length === 0 && empty && (
          <tr>
            <td colSpan={span} className="py-3 text-gray-400">
              {empty}
            </td>
          </tr>
        )}
      </tbody>
    </table>
    {left > 0 && (
      <div ref={moreRef} className="flex items-center gap-2 py-2 text-xs text-gray-500 dark:text-gray-400" data-testid="table-more">
        <span>
          {shown.length.toLocaleString()} of {ordered.length.toLocaleString()} rows shown
        </span>
        <button type="button" onClick={() => setLimit(n => n + step)} className="underline hover:no-underline">
          Show {Math.min(step, left)} more
        </button>
        <button type="button" onClick={() => setLimit(ordered.length)} className="underline hover:no-underline">
          Show all
        </button>
      </div>
    )}
    </>
  );
}
